import { GenericFieldArray } from "@/lib/Components/Form/FieldArray/GenericFieldArray";
import { SwitchInput } from "@/lib/Components/Form/Inputs/SwitchInput";
import { useFormState } from "@/lib/Components/Form/Hooks/useFormState";
import { ClosedDaysSchema } from "@/app/Suppliers/Utils/mapClosedDays";
import dayjs from "dayjs";
import { CalendarDaysIcon } from "@heroicons/react/24/outline";
import { ReactNode } from "react";
import { DateInput } from "@/lib/Components/Form/Inputs/DateInput";

export function ClosedDatesForm({ emptyState }: { emptyState: ReactNode }) {
  const { values, isEditing } = useFormState<{
    closed_dates: ClosedDaysSchema[];
  }>();

  const repeatingDates = values.closed_dates.filter((date) => date.repeating);
  const nonRepeatingDates = values.closed_dates.filter(
    (date) => !date.repeating,
  );

  if (!isEditing) {
    if (values.closed_dates.length === 0) {
      return (
        <div className="col-span-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="p-6">{emptyState}</div>
        </div>
      );
    }

    return (
      <div className="col-span-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="space-y-6 p-6">
          {repeatingDates.length > 0 && (
            <div>
              <h3 className="mb-3 text-sm font-medium text-slate-900">
                Repeating dates
              </h3>
              <div className="flex flex-wrap gap-2">
                {repeatingDates.map((date) => (
                  <div
                    key={date.date}
                    className="inline-flex items-center gap-2 rounded-lg bg-orange-50 px-3 py-2 text-sm"
                  >
                    <CalendarDaysIcon className="h-4 w-4 text-orange-600" />
                    <span className="font-semibold text-orange-900">
                      {dayjs(date.date).format("DD MMM")}
                    </span>
                    <span className="text-orange-700">(yearly)</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {nonRepeatingDates.length > 0 && (
            <div>
              <h3 className="mb-3 text-sm font-medium text-slate-900">
                One-off dates
              </h3>
              <div className="flex flex-wrap gap-2">
                {nonRepeatingDates.map((date) => (
                  <div
                    key={date.date}
                    className="inline-flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm"
                  >
                    <CalendarDaysIcon className="h-4 w-4 text-slate-600" />
                    <span className="font-semibold text-slate-900">
                      {dayjs(date.date).format("DD MMM YYYY")}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <GenericFieldArray
        fieldArrayKey={"closed_dates"}
        label="Closed dates"
        newLineItemValues={{
          date: dayjs().format("YYYY-MM-DD"),
          repeating: false,
        }}
        Icon={CalendarDaysIcon}
        lineItemLabel={"closed date"}
      >
        {(item, index) => {
          return (
            <>
              <DateInput
                name={`closed_dates.${index}.date`}
                className="col-span-4"
                label="Date"
              />
              <SwitchInput
                name={`closed_dates.${index}.repeating`}
                label="Repeating"
              />
            </>
          );
        }}
      </GenericFieldArray>
    </>
  );
}
